import {
    AfterViewChecked,
    ChangeDetectorRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    AbstractControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';

import { INDDFormsCustomValidatorFn } from '../ndd-ng-form-error/shared/ndd-ng-form-error.model';
import { NDDFormErrorService } from '../ndd-ng-form-error/shared/ndd-ng-form-error.service';
import { NDDFormsBuilderService } from './ndd-ng-forms-builder.service';

export class NDDFormsBaseComponent implements OnChanges, AfterViewChecked, OnDestroy {
    /** Formulário. */
    @Input() public controlForm: FormGroup;

    /** Id do controle. */
    @Input() public controlId: string;
    /** Nome do controle (usado para criação do controle no formulário e para o atributo HTML `name`). */
    @Input() public controlKey: string;
    /** Texto da label do controle. */
    @Input() public controlLabel?: string;
    /** Valor do controle. */
    @Input() public controlValue?: any;
    /** Define uma descrição para o campo */
    @Input() public controlDescription?: string;
    /** Define uma lista de descrições para o campo */
    @Input() public controlDescriptions?: string[];
    /** Mensagem inválida personalizada. */
    @Input() public controlCustomInvalidMessage?: string;
    /** Define exibição da mensagem inválida personalizada. */
    @Input() public controlShowCustomInvalidMessage?: boolean;

    /** Define controle como foco inicial. */
    @Input() public controlAutofocus?: boolean;
    /** Define controle como desabilitado, na inicialização. */
    @Input() public controlDisabled?: boolean;
    /** Define controle como obrigatório. */
    @Input() public controlRequired?: boolean;
    /** Tamanho para o controle. */
    @Input() public controlWidth: string = '100%';
    /** Lista de validadores personalizados. */
    @Input() public controlCustomValidators?: INDDFormsCustomValidatorFn[];

    /** Evento de alteração do valor. */
    @Output() public controlValueChange: EventEmitter<any> = new EventEmitter();
    /** Evento `focusin`. */
    @Output() public onControlFocusIn: EventEmitter<null> = new EventEmitter();
    /** Evento `focusout`. */
    @Output() public onControlFocusOut: EventEmitter<null> = new EventEmitter();

    protected ngUnsubscribe: Subject<void> = new Subject<void>();
    /** Controle. */
    protected control: AbstractControl;
    /** Mensagem inválida. */
    protected invalidMessage: string;

    /** Cache de validadores. */
    private validators: ValidatorFn[] = [];

    constructor(private formErrorService: NDDFormErrorService, private cdr: ChangeDetectorRef) { }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.control) {
            if (changes.controlDisabled && changes.controlDisabled.currentValue !== changes.controlDisabled.previousValue) {
                if (this.controlDisabled) {
                    this.control.disable();

                    return;
                }

                this.control.enable();
            }
            if (changes.controlCustomValidators && changes.controlCustomValidators.currentValue) {
                this.validators = this.validators.concat(changes.controlCustomValidators.currentValue);
                NDDFormsBuilderService.addValidators(this.control, this.validators);
            }
        }
    }

    public ngAfterViewChecked(): void {
        this.setInvalidMessage();
    }

    public ngOnDestroy(): void {
        this.controlForm.removeControl(this.controlKey);

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    @HostListener('focusin')
    public focusin(): void {
        this.onControlFocusIn.emit();
    }

    @HostListener('focusout')
    public focusout(): void {
        this.onControlFocusOut.emit();
    }

    /**
     * Adiciona o controle no formulário.
     *
     * @protected
     * @memberof NDDFormsBaseComponent
     */
    protected addControl(): void {
        if (!this.controlForm) { throw new Error('É necessário definir `controlForm` para elementos de formulário.'); }
        if (!this.controlId) {
            throw new Error('É necessário definir `controlId` para elementos de formulário.');
        }
        if (!this.controlKey) { throw new Error('É necessário definir `controlKey` para elementos de formulário.'); }

        this.control = NDDFormsBuilderService.addControl(this.controlForm, this);

        if (this.controlRequired) {
            this.cacheValidator(Validators.required);
        }

        if (this.controlCustomValidators && this.controlCustomValidators.length) {
            this.controlCustomValidators.forEach((validator: ValidatorFn) => {
                this.cacheValidator(validator);
            });
        }
    }

    /**
     * Salva em cache cada validador, antes de defini-los no controle (após, usar `addValidators()` para defini-los).
     *
     * @protected
     * @param {ValidatorFn} validator Validador.
     * @memberof NDDFormsBaseComponent
     */
    protected cacheValidator(validator: ValidatorFn): void {
        this.validators.push(validator);
    }

    /**
     * Define no controle os validadores salvos em cache.
     *
     * @protected
     * @memberof NDDFormsBaseComponent
     */
    protected addValidators(): void {
        NDDFormsBuilderService.addValidators(this.control, this.validators);
    }

    /**
     * Marca o input como `touched` para atualizar as validações.
     *
     * @protected
     * @memberof NDDFormsBaseComponent
     */
    protected updateValidity(): void {
        if (!this.control.touched) {
            this.control.markAsTouched();
        }
    }

    /** Define mensagem inválida. */
    private setInvalidMessage(): void {
        if (this.controlShowCustomInvalidMessage && this.controlCustomInvalidMessage) {
            // Define mensagem personalizada.
            this.invalidMessage = this.controlCustomInvalidMessage;
        } else {
            // Define mensagem através de `NDDFormsBuilderService`.
            this.invalidMessage = NDDFormsBuilderService.generateInvalidMessage(this.control, this.formErrorService);
        }

        this.cdr.detectChanges();
    }
}
